/**
 * Quote PDF generator.
 *
 * Listens for clicks on [data-quote-trigger] elements anywhere on the page,
 * collects the relevant items (product page: from embedded JSON; cart: via
 * /cart.js), opens the <quote-modal> dialog, and on submit POSTs to /contact
 * and downloads a Letter-size PDF built with jsPDF + autoTable.
 */

const JSPDF_SRC = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
const AUTOTABLE_SRC = 'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.0/dist/jspdf.plugin.autotable.min.js';

let libsLoadingPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

function ensureLibsLoaded() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve();
  if (libsLoadingPromise) return libsLoadingPromise;
  libsLoadingPromise = loadScript(JSPDF_SRC)
    .then(() => loadScript(AUTOTABLE_SRC));
  return libsLoadingPromise;
}

function fetchAsDataURL(url) {
  return fetch(url, { mode: 'cors' })
    .then((r) => {
      if (!r.ok) throw new Error('Image fetch failed: ' + r.status);
      return r.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        })
    )
    .catch((err) => {
      console.warn('[quote] image fetch failed for', url, err);
      return null;
    });
}

function getImageNaturalSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = dataUrl;
  });
}

function shopifyImageUrl(src, width) {
  if (!src) return '';
  // Insert _{width}x before the extension on Shopify CDN URLs.
  // Handles both protocol-relative and absolute URLs.
  return src.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, `_${width}x$1$2`);
}

function generateQuoteNumber() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  let suffix;
  if (window.crypto && window.crypto.randomUUID) {
    suffix = window.crypto.randomUUID().replace(/-/g, '').slice(0, 4).toUpperCase();
  } else {
    suffix = Math.random().toString(16).slice(2, 6).toUpperCase();
  }
  return `Q-${yyyy}${mm}${dd}-${suffix}`;
}

function formatCurrency(cents, currencyCode) {
  const amount = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat(navigator.language || 'en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
    }).format(amount);
  } catch (_) {
    return '$' + amount.toFixed(2);
  }
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function buildItemFromProductElement(triggerEl) {
  const jsonId = triggerEl.dataset.productJsonId;
  if (!jsonId) return null;
  const tag = document.getElementById(jsonId);
  if (!tag) return null;
  let product;
  try {
    product = JSON.parse(tag.textContent);
  } catch (_) {
    return null;
  }

  // Find the active product form to read selected variant + qty.
  const productForm = triggerEl.closest('product-form') || document.querySelector('product-form');
  let variantId = null;
  let qty = 1;
  if (productForm) {
    const idInput = productForm.querySelector('[name="id"]');
    if (idInput && idInput.value) variantId = parseInt(idInput.value, 10);
    const qtyInput = productForm.querySelector('[name="quantity"]');
    if (qtyInput && qtyInput.value) qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
  }

  const variant =
    product.variants.find((v) => v.id === variantId) ||
    product.variants.find((v) => v.available) ||
    product.variants[0];

  if (!variant) return null;

  const image = variant.featured_image || (product.featured_image ? { src: product.featured_image } : null);

  return {
    title: product.title,
    variantTitle: variant.title === 'Default Title' ? '' : variant.title,
    sku: variant.sku || '',
    qty: qty,
    unitPriceCents: variant.price,
    lineTotalCents: variant.price * qty,
    imageSrc: image ? image.src : '',
  };
}

async function buildItemsFromCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Cart fetch failed');
  const cart = await res.json();
  return cart.items.map((item) => ({
    title: item.product_title,
    variantTitle: item.variant_title || '',
    sku: item.sku || '',
    qty: item.quantity,
    unitPriceCents: item.price,
    lineTotalCents: item.final_line_price,
    imageSrc: item.image || '',
  }));
}

class QuoteModal extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.lastPdfBlob = null;
    this.lastPdfFilename = null;
    this.previouslyFocused = null;
  }

  connectedCallback() {
    this.form = this.querySelector('#QuoteModalForm');
    this.itemsList = this.querySelector('[data-quote-items-list]');
    this.errorEl = this.querySelector('[data-quote-error]');
    this.successEl = this.querySelector('[data-quote-success]');
    this.warningEl = this.querySelector('[data-quote-warning]');
    this.submitBtn = this.querySelector('[data-quote-submit]');
    this.submitLabel = this.querySelector('.quote-modal__submit-label');
    this.submitSpinner = this.querySelector('.quote-modal__submit-spinner');
    this.submitSuccess = this.querySelector('.quote-modal__submit-success');
    this.dialog = this.querySelector('.quote-modal__dialog');

    document.addEventListener('click', this._onDocClick.bind(this));
    this.querySelectorAll('[data-quote-close]').forEach((el) =>
      el.addEventListener('click', this.close.bind(this))
    );
    this.form.addEventListener('submit', this._onSubmit.bind(this));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });
    const redownload = this.querySelector('[data-quote-redownload]');
    if (redownload) {
      redownload.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.lastPdfBlob && this.lastPdfFilename) {
          this._triggerDownload(this.lastPdfBlob, this.lastPdfFilename);
        }
      });
    }
  }

  _onDocClick(e) {
    const trigger = e.target.closest('[data-quote-trigger]');
    if (!trigger) return;
    e.preventDefault();
    this._openFromTrigger(trigger).catch((err) => {
      console.error('[quote] failed to open', err);
      this._showError("Couldn't load your cart. Please refresh and try again.");
      this.open();
    });
  }

  async _openFromTrigger(trigger) {
    const source = trigger.dataset.source;
    let items;
    if (source === 'product') {
      const item = buildItemFromProductElement(trigger);
      items = item ? [item] : [];
    } else {
      items = await buildItemsFromCart();
    }
    if (!items.length) {
      this._showError('There are no items to quote.');
      this.open();
      return;
    }
    this.items = items;
    this._renderItems();
    this.open();
  }

  _renderItems() {
    const currency = this.dataset.shopCurrency || 'USD';
    this.itemsList.innerHTML = this.items
      .map((it) => {
        const variant = it.variantTitle ? `<span class="quote-modal__item-variant"> — ${escapeHtml(it.variantTitle)}</span>` : '';
        const thumb = it.imageSrc
          ? `<img class="quote-modal__item-thumb" src="${escapeHtml(shopifyImageUrl(it.imageSrc, 120))}" alt="" loading="lazy" />`
          : `<span class="quote-modal__item-thumb quote-modal__item-thumb--empty" aria-hidden="true"></span>`;
        return `<li class="quote-modal__item">
          ${thumb}
          <span class="quote-modal__item-text">
            <span class="quote-modal__item-title">${escapeHtml(it.title)}${variant}</span>
            <span class="quote-modal__item-meta">Qty ${it.qty} × ${formatCurrency(it.unitPriceCents, currency)}</span>
          </span>
        </li>`;
      })
      .join('');
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.hidden = false;
    document.body.classList.add('quote-modal-open');
    this.successEl.hidden = true;
    this.form.hidden = false;
    this.errorEl.hidden = true;
    this._setButtonState('idle');
    // Focus first field next tick, but don't steal focus from a field
    // the user already clicked into (breaks password-manager dropdowns).
    requestAnimationFrame(() => {
      if (this.contains(document.activeElement)) return;
      const firstField = this.form.querySelector('input,textarea,button');
      if (firstField) firstField.focus();
    });
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('quote-modal-open');
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  _showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  _setButtonState(state) {
    // state: 'idle' | 'submitting' | 'success'
    this.submitBtn.disabled = state !== 'idle';
    this.submitBtn.classList.toggle('is-success', state === 'success');
    this.submitLabel.hidden = state !== 'idle';
    this.submitSpinner.hidden = state !== 'submitting';
    this.submitSuccess.hidden = state !== 'success';
  }

  async _onSubmit(e) {
    e.preventDefault();
    this.errorEl.hidden = true;

    const data = new FormData(this.form);
    const name = (data.get('contact[name]') || '').toString().trim();
    const email = (data.get('contact[email]') || '').toString().trim();
    if (!name || !email) {
      this._showError('Name and email are required.');
      return;
    }

    this._setButtonState('submitting');
    const quoteNumber = generateQuoteNumber();
    const validForDays = parseInt(this.dataset.validForDays, 10) || 30;
    const issueDate = new Date();
    const validUntil = new Date(issueDate.getTime() + validForDays * 86400000);

    const formInfo = {
      name: name,
      company: (data.get('contact[company_name]') || '').toString(),
      email: email,
      phone: (data.get('contact[phone]') || '').toString(),
      project: (data.get('contact[project-name]') || '').toString(),
      deliveryDate: (data.get('contact[delivery-date]') || '').toString(),
      instructions: (data.get('instructions') || '').toString(),
    };

    let contactPostFailed = false;
    try {
      await this._postToContact(quoteNumber, formInfo);
    } catch (err) {
      console.warn('[quote] contact form post failed', err);
      contactPostFailed = true;
    }

    let pdfBlob;
    let pdfFilename;
    try {
      await ensureLibsLoaded();
      const out = await this._buildPdf(quoteNumber, issueDate, validUntil, formInfo);
      pdfBlob = out.blob;
      pdfFilename = out.filename;
    } catch (err) {
      console.error('[quote] pdf generation failed', err);
      this._setButtonState('idle');
      this._showError('Something went wrong generating your quote. Please try again.');
      return;
    }

    this.lastPdfBlob = pdfBlob;
    this.lastPdfFilename = pdfFilename;
    this._triggerDownload(pdfBlob, pdfFilename);

    this._setButtonState('success');
    this.successEl.hidden = false;
    if (contactPostFailed) {
      this.warningEl.textContent =
        "We couldn't notify our team this time, but your PDF is ready.";
      this.warningEl.hidden = false;
    } else {
      this.warningEl.hidden = true;
    }
  }

  _postToContact(quoteNumber, info) {
    const currency = this.dataset.shopCurrency || 'USD';
    const lines = this.items
      .map((it, i) => {
        const variant = it.variantTitle ? ` (${it.variantTitle})` : '';
        const sku = it.sku ? ` SKU ${it.sku} —` : '';
        return `${i + 1}. ${it.title}${variant} —${sku} Qty ${it.qty} — ${formatCurrency(
          it.unitPriceCents,
          currency
        )} ea — ${formatCurrency(it.lineTotalCents, currency)}`;
      })
      .join('\n');
    const subtotalCents = this.items.reduce((s, it) => s + it.lineTotalCents, 0);
    const body =
      (info.instructions ? info.instructions + '\n\n' : '') +
      `--- Items ---\n${lines}\nSubtotal: ${formatCurrency(subtotalCents, currency)}`;

    // Build FormData from the rendered {% form 'contact' %} so Shopify's
    // injected hidden fields (form_type, utf8, bot-protection tokens) are
    // included. Then add the dynamic computed fields on top.
    const fd = new FormData(this.form);
    fd.delete('instructions');
    fd.set('contact[quote_number]', quoteNumber);
    fd.set('contact[body]', body);

    const action = this.form.getAttribute('action') || '/contact';
    return fetch(action, {
      method: 'POST',
      body: fd,
      redirect: 'follow',
      credentials: 'same-origin',
      headers: { Accept: 'text/html,application/xhtml+xml' },
    }).then((r) => {
      if (!r.ok) {
        console.error(
          '[quote] /contact returned',
          r.status,
          r.statusText,
          'final URL:',
          r.url
        );
        throw new Error('Contact post non-ok: ' + r.status);
      }
      return r;
    });
  }

  async _buildPdf(quoteNumber, issueDate, validUntil, info) {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    const currency = this.dataset.shopCurrency || 'USD';

    // --- Header ---
    let y = margin;

    if (this.dataset.logoUrl) {
      const logoData = await fetchAsDataURL(this.dataset.logoUrl);
      if (logoData) {
        try {
          const natural = await getImageNaturalSize(logoData);
          const maxW = 160;
          const maxH = 70;
          let lw = maxW;
          let lh = maxH;
          if (natural.w && natural.h) {
            const ratio = natural.w / natural.h;
            if (ratio >= maxW / maxH) {
              lw = maxW;
              lh = maxW / ratio;
            } else {
              lh = maxH;
              lw = maxH * ratio;
            }
          }
          const fmt = /^data:image\/(png|jpeg|jpg|webp)/i.exec(logoData);
          const fmtStr = fmt ? fmt[1].toUpperCase().replace('JPG', 'JPEG') : 'PNG';
          doc.addImage(logoData, fmtStr, margin, y, lw, lh, undefined, 'FAST');
        } catch (err) {
          console.warn('[quote] failed to render logo', err);
        }
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('QUOTE', pageWidth - margin, y + 18, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(quoteNumber, pageWidth - margin, y + 36, { align: 'right' });
    doc.text(`Date: ${formatDate(issueDate)}`, pageWidth - margin, y + 50, { align: 'right' });
    doc.text(`Valid until: ${formatDate(validUntil)}`, pageWidth - margin, y + 64, { align: 'right' });

    y += 90;

    // --- Sender / Recipient ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(this.dataset.shopName || 'Too Many Amps', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let senderY = y + 14;
    const addr = (this.dataset.businessAddress || '').split(/\r?\n/).filter(Boolean);
    addr.forEach((line) => {
      doc.text(line, margin, senderY);
      senderY += 12;
    });
    if (this.dataset.businessPhone) {
      doc.text(this.dataset.businessPhone, margin, senderY);
      senderY += 12;
    }
    if (this.dataset.businessEmail) {
      doc.text(this.dataset.businessEmail, margin, senderY);
      senderY += 12;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Quote For:', pageWidth / 2 + 20, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let recipY = y + 14;
    const recipientLines = [
      info.name,
      info.company,
      info.email,
      info.phone,
      info.project ? `Project: ${info.project}` : '',
      info.deliveryDate ? `Needed: ${info.deliveryDate}` : '',
    ].filter(Boolean);
    recipientLines.forEach((line) => {
      doc.text(line, pageWidth / 2 + 20, recipY);
      recipY += 12;
    });

    y = Math.max(senderY, recipY) + 10;

    // --- Line items table ---
    const thumbCache = {};
    await Promise.all(
      this.items.map(async (it) => {
        if (!it.imageSrc) return;
        const url = shopifyImageUrl(it.imageSrc, 200);
        thumbCache[it.imageSrc] = await fetchAsDataURL(url);
      })
    );

    const subtotalCents = this.items.reduce((s, it) => s + it.lineTotalCents, 0);

    const body = this.items.map((it) => {
      const desc = it.variantTitle ? `${it.title}\n${it.variantTitle}` : it.title;
      return [
        '', // image cell drawn in didDrawCell
        desc,
        it.sku,
        String(it.qty),
        formatCurrency(it.unitPriceCents, currency),
        formatCurrency(it.lineTotalCents, currency),
      ];
    });

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['', 'Description', 'SKU', 'Qty', 'Price', 'Total']],
      body: body,
      styles: { fontSize: 9, cellPadding: 6, valign: 'middle' },
      headStyles: { fillColor: [17, 17, 17], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 50, minCellHeight: 50 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 70 },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 65, halign: 'right' },
        5: { cellWidth: 65, halign: 'right' },
      },
      didDrawCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 0) return;
        const item = this.items[data.row.index];
        if (!item || !item.imageSrc) return;
        const dataUrl = thumbCache[item.imageSrc];
        if (!dataUrl) return;
        const size = Math.min(data.cell.width - 4, data.cell.height - 4, 44);
        const cx = data.cell.x + (data.cell.width - size) / 2;
        const cy = data.cell.y + (data.cell.height - size) / 2;
        try {
          doc.addImage(dataUrl, 'JPEG', cx, cy, size, size, undefined, 'FAST');
        } catch (_) {
          // ignore
        }
      },
    });

    let endY = doc.lastAutoTable.finalY + 14;

    // --- Subtotal ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Subtotal: ${formatCurrency(subtotalCents, currency)}`, pageWidth - margin, endY, {
      align: 'right',
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('(Tax and shipping not included)', pageWidth - margin, endY + 12, { align: 'right' });
    endY += 32;

    // --- Special instructions ---
    if (info.instructions) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Special Instructions:', margin, endY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const wrapped = doc.splitTextToSize(info.instructions, pageWidth - margin * 2);
      doc.text(wrapped, margin, endY + 14);
      endY += 14 + wrapped.length * 11 + 10;
    }

    // --- Footer text (anchored to bottom of last page, centered) ---
    const footerText = this.dataset.footerText;
    if (footerText) {
      const pageCount = doc.getNumberOfPages();
      doc.setPage(pageCount);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const pageHeight = doc.internal.pageSize.getHeight();
      const wrapped = doc.splitTextToSize(footerText, pageWidth - margin * 2);
      const lineHeight = 10;
      const totalHeight = wrapped.length * lineHeight;
      const footerY = pageHeight - margin - totalHeight + lineHeight;
      doc.text(wrapped, pageWidth / 2, footerY, { align: 'center' });
    }

    const filename = `TMA-Quote-${quoteNumber}.pdf`;
    const blob = doc.output('blob');
    return { blob, filename };
  }

  _triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
}

if (!customElements.get('quote-modal')) {
  customElements.define('quote-modal', QuoteModal);
}
